import React from 'react';
import type {
  CampaignLeaderboardEntry,
  CampaignRoundLeaderboard,
  QuizCampaignLeaderboardResponse,
} from '../types';
import { Trophy, Clock, Award, Download } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../components/AuthProvider';
import { downloadCsv } from '../lib/csv';
import { cn } from '../lib/utils';
import { shouldShowCampaignSponsorBlock } from '../lib/sponsor';
import { CampaignSponsorBlock } from '../components/quiz/CampaignSponsorBlock';
import { CampaignSponsorLogo } from '../components/quiz/CampaignSponsorLogo';
import { campaignHeaderSubtitle } from '../lib/campaign-header';
import { useApiJson } from '../hooks/useApi';
import { MarkdownContent } from '../components/markdown/MarkdownContent';

const TOP_N = 10;

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

interface LeaderboardRowProps {
  entry: CampaignLeaderboardEntry;
  isCurrentUser?: boolean;
  variant?: 'default' | 'your-rank';
}

function LeaderboardRow({ entry, isCurrentUser, variant = 'default' }: LeaderboardRowProps) {
  const isTopRank = entry.rank === 1;

  return (
    <div
      className={cn(
        'flex items-center gap-4 group rounded-[var(--radius-md)] px-2 py-1.5 -mx-2',
        isCurrentUser && variant === 'default' && 'bg-[var(--color-accent-subtle)] ring-1 ring-[var(--color-accent)]/30',
        variant === 'your-rank' && 'bg-[var(--color-surface-2)] border border-[var(--color-border)] px-3 py-2.5 -mx-0',
      )}
    >
      <div
        className={cn(
          'w-8 h-8 rounded-[var(--radius-sm)] flex items-center justify-center font-semibold text-xs shrink-0',
          isTopRank
            ? 'bg-[var(--color-accent)] text-white'
            : isCurrentUser
              ? 'bg-[var(--color-accent)]/15 text-[var(--color-accent)]'
              : 'bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)]',
        )}
      >
        {entry.rank}
      </div>
      <div className="flex-1 flex items-center justify-between gap-4 min-w-0">
        <div className="min-w-0">
          {variant === 'your-rank' && (
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-accent)] mb-0.5">
              Your rank
            </p>
          )}
          <p
            className={cn(
              'font-medium truncate transition-colors duration-[var(--duration-fast)]',
              isCurrentUser
                ? 'text-[var(--color-accent)]'
                : 'text-[var(--color-text-primary)] group-hover:text-[var(--color-accent)]',
            )}
          >
            {entry.name}
            {isCurrentUser && variant === 'default' ? ' (You)' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-[var(--color-text-tertiary)] font-medium hidden sm:inline">
            {entry.sessionsCompleted} round{entry.sessionsCompleted === 1 ? '' : 's'} ·{' '}
            {formatDuration(entry.totalDurationSeconds)}
          </span>
          <span className="px-2.5 py-1 bg-[var(--color-accent-subtle)] text-[var(--color-accent)] rounded-[var(--radius-sm)] font-semibold text-sm tabular-nums">
            {entry.totalPoints} pts
          </span>
        </div>
      </div>
    </div>
  );
}

export function LeaderboardPage() {
  const { member, isAdmin } = useAuth();
  const { data: lbData, loading } = useApiJson<QuizCampaignLeaderboardResponse>('/api/quiz-campaigns/leaderboard');
  const [boardMode, setBoardMode] = React.useState<'round' | 'overall'>('round');
  const [selectedSessionId, setSelectedSessionId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isAdmin && boardMode !== 'round') {
      setBoardMode('round');
    }
  }, [boardMode, isAdmin]);

  React.useEffect(() => {
    const defaultSessionId =
      lbData?.session?.id ?? lbData?.roundLeaderboards[0]?.session.id ?? null;
    setSelectedSessionId((current) => {
      if (current && lbData?.roundLeaderboards.some((board) => board.session.id === current)) {
        return current;
      }
      return defaultSessionId;
    });
  }, [lbData]);

  const campaign = lbData?.campaign ?? null;
  const roundLeaderboards = lbData?.roundLeaderboards ?? [];
  const selectedRoundBoard =
    roundLeaderboards.find((board) => board.session.id === selectedSessionId) ??
    roundLeaderboards[0] ??
    null;
  const isOverallView = isAdmin && boardMode === 'overall';
  const leaderboard = isOverallView
    ? lbData?.overallLeaderboard ?? []
    : selectedRoundBoard?.leaderboard ?? lbData?.leaderboard ?? [];
  const top10 = leaderboard.slice(0, TOP_N);
  const displayedEntries = isAdmin ? leaderboard : top10;
  const currentUserEntry = member?.uid
    ? leaderboard.find((entry) => entry.uid === member.uid)
    : undefined;
  const showCurrentUserOutside = !isAdmin && currentUserEntry != null && currentUserEntry.rank > TOP_N;
  const subtitle = campaign?.description
    ? campaign.description
    : campaign
      ? isOverallView
        ? 'Overall standings across all rounds — highest points, then fastest total time.'
        : `${selectedRoundBoard?.session.label ?? 'Round'} standings — highest points, then fastest total time.`
      : 'Final standings will appear when a quiz is active.';
  const boardTitle = campaign
    ? isOverallView
      ? `Overall — ${campaign.title}`
      : `${selectedRoundBoard?.session.label ?? 'Round'} — ${campaign.title}`
    : 'Standings';
  const handleExportLeaderboard = () => {
    if (!campaign || leaderboard.length === 0) return;
    const label = isOverallView ? 'overall' : (selectedRoundBoard?.session.label ?? 'round');
    const filenameLabel = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadCsv(
      `leaderboard-${campaign.slug}-${filenameLabel || 'round'}-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Rank', 'Member UID', 'Name', 'Points', 'Duration Seconds', 'Rounds Completed', 'Submitted At'],
      leaderboard.map((entry) => [
        entry.rank,
        entry.uid,
        entry.name,
        entry.totalPoints,
        entry.totalDurationSeconds,
        entry.sessionsCompleted,
        entry.earliestSubmittedAt ?? '',
      ]),
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="text-center space-y-4">
          <CampaignSponsorLogo
            sponsorLogoUrl={campaign?.sponsorLogoUrl}
            sponsorNames={campaign?.sponsorNames}
            size="prominent"
          />
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {campaign?.title ?? 'Leaderboard'}
          </h1>
          <MarkdownContent
            text={subtitle}
            className="text-[var(--color-text-secondary)] text-sm mt-1"
          />
          {campaign &&
          shouldShowCampaignSponsorBlock(campaign.sponsorNames, campaign.sponsorLinkUrl) && (
            <CampaignSponsorBlock
              sponsorNames={campaign.sponsorNames}
              sponsorLinkUrl={campaign.sponsorLinkUrl}
            />
          )}
        </header>

        <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] overflow-hidden">
          <div className="bg-[var(--color-surface-2)] p-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Trophy className="text-[var(--color-accent)] w-5 h-5" />
              <h3 className="font-semibold text-[var(--color-text-primary)]">
                {isAdmin ? boardTitle : campaign ? `Top ${TOP_N} — ${boardTitle}` : 'Standings'}
              </h3>
            </div>
            <Clock className="w-5 h-5 text-[var(--color-text-tertiary)]" />
          </div>
          <div className="p-4 md:p-6">
            {isAdmin && campaign && (
              <div className="space-y-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-1">
                    {(['round', 'overall'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setBoardMode(mode)}
                        className={cn(
                          'px-3 py-1.5 text-sm font-medium rounded-[calc(var(--radius-md)-4px)] transition-colors',
                          boardMode === mode
                            ? 'bg-[var(--color-accent)] text-white'
                            : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                        )}
                      >
                        {mode === 'round' ? 'Round-wise' : 'Overall'}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleExportLeaderboard}
                    disabled={leaderboard.length === 0}
                    className="inline-flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-surface-2)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    Download CSV
                  </button>
                </div>

                {boardMode === 'round' && roundLeaderboards.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {roundLeaderboards.map((board: CampaignRoundLeaderboard) => (
                      <button
                        key={board.session.id}
                        type="button"
                        onClick={() => setSelectedSessionId(board.session.id)}
                        className={cn(
                          'px-3 py-1.5 rounded-[var(--radius-md)] border text-sm transition-colors',
                          selectedSessionId === board.session.id
                            ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                            : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                        )}
                      >
                        {board.session.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {loading && (
              <p className="text-center py-10 text-[var(--color-text-tertiary)] text-sm">Loading…</p>
            )}

            {!loading && leaderboard.length > 0 ? (
              <div className="space-y-3">
                {displayedEntries.map((entry) => (
                  <div key={entry.uid}>
                    <LeaderboardRow
                      entry={entry}
                      isCurrentUser={entry.uid === member?.uid}
                    />
                  </div>
                ))}

                {showCurrentUserOutside && currentUserEntry ? (
                  <>
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 border-t border-dashed border-[var(--color-border)]" />
                      <span className="text-xs text-[var(--color-text-tertiary)] shrink-0">···</span>
                      <div className="flex-1 border-t border-dashed border-[var(--color-border)]" />
                    </div>
                    <div key={currentUserEntry.uid}>
                      <LeaderboardRow entry={currentUserEntry} isCurrentUser variant="your-rank" />
                    </div>
                  </>
                ) : null}
              </div>
            ) : !loading ? (
              <div className="text-center py-10 text-[var(--color-text-tertiary)]">
                <Award className="w-12 h-12 mx-auto mb-2" />
                <p>
                  {isOverallView
                    ? 'Overall leaderboard will appear after participants complete at least one round.'
                    : 'Round leaderboard will appear after the first quiz attempts.'}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Layout>
  );
}
